import { useNavigate } from 'react-router-dom';

import { Chip, TableCell, TableRow } from '@mui/material';

import { reInitiateCustomerOnboarding } from '../../api/clientsApi/clientsApi';
import useSnackBar from '../../hooks/useSnackBar';
import { useHasPermission } from '../../hooks/useHasPermission';
import { formatDate } from '../../helpers/formatDate';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { ClientData } from '../../types';
import OnboardingStatusStack from '../../components/OnboardingStatusStack';

interface ClientsTableDataRowProps {
  tableData: ClientData[];
}

const ClientsTableDataRow = ({ tableData }: ClientsTableDataRowProps) => {
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();
  const canEdit = useHasPermission('Clients', 'Edit');
  const showUserOnboardingDetails = localStorage.getItem('showUserOnboardingDetails') === 'true';

  const onTableRowClick = (id: number) => {
    navigate(`/clients/${String(id)}/home`);
  };

  const handleResendInvite = (customerId?: number, settlementId?: number) => {
    if (!customerId) {
      setAlert((prev) => ({
        ...prev,
        message: 'Customer id is not available for re-initiating onboarding.',
        type: 'error',
      }));
      return;
    }

    reInitiateCustomerOnboarding(customerId, settlementId)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message:
            'The Onboarding process has been re-initiated. Please check your email for more information.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const formattedClientsList = tableData.map((client) => {
    // Onboarding is settlement based: resend is only offered here when the client has a single settlement.
    const settlementInvites = client.settlementOnboardingInvites ?? [];
    const singleSettlementInvite = settlementInvites.length === 1 ? settlementInvites[0] : null;

    return {
      id: client.id,
      onboardingStatuses: client.onboardingStatuses,
      rowFields: {
        name: `${client.firstName} ${client.middleName || ''} ${client.lastName}`.replace(
          /\s+/g,
          ' '
        ).trim(),
        referenceId: client.referenceId,
        ...(showUserOnboardingDetails && {
          onboardingStatus: client.onboardingStatus,
        }),
        email: client.email,
        // createdAt: formatDate(client.createdAt),
        updatedAt: formatDate(client.updatedAt),
        activeCases: client.additonalInfo.activeCasesCount,
        grossSettlements: numberToUSDCurrency(client.additonalInfo.grossSettlements),
        netSettlements: numberToUSDCurrency(client.additonalInfo.netSettlements),
        walletBalance: '-',
        ...(showUserOnboardingDetails && {
          actions: singleSettlementInvite && (
            <Chip
              label="Resend Invite"
              variant="outlined"
              disabled={singleSettlementInvite.resendOnboardingInvite !== true || !canEdit}
              onClick={() =>
                handleResendInvite(client.customerId, singleSettlementInvite.settlementId)
              }
              sx={{
                height: '25px',
                borderRadius: '12px',
                border: '1px solid #38BDF8',
                background: '#38BDF814',
                width: 'fit-content',
                '& .MuiChip-label': {
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#38BDF8',
                  padding: '4px 12px',
                },
                '&.Mui-disabled': {
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.08)',
                },
                '&.Mui-disabled .MuiChip-label': {
                  color: 'rgba(255, 255, 255, 0.35)',
                },
              }}
            />
          ),
        }),
      },
    };
  });

  return (
    <>
      {formattedClientsList.map((tableItem) => (
        <TableRow
          key={tableItem.id}
          hover
          onClick={() => onTableRowClick(tableItem.id)}
          sx={{ cursor: 'pointer' }}
        >
          {Object.entries(tableItem.rowFields).map(([key, value], idx) => {
            if (key === 'onboardingStatus') {
              return (
                <TableCell key={idx}>
                  <OnboardingStatusStack
                    status={String(value)}
                    items={tableItem.onboardingStatuses}
                    maxVisible={3}
                  />
                </TableCell>
              );
            }

            if (key === 'actions') {
              return (
                <TableCell
                  key={idx}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  {value}
                </TableCell>
              );
            }

            return <TableCell key={idx}>{value}</TableCell>;
          })}
        </TableRow>
      ))}
    </>
  );
};

export default ClientsTableDataRow;
